import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { TaskSwipeCard, TaskCardData } from '@/components/swipe/TaskSwipeCard';
import { SuggestionCard, SuggestionData } from '@/components/swipe/SuggestionCard';
import { InsightCard, InsightData } from '@/components/swipe/InsightCard';
import { FeedEmpty } from '@/components/feed/FeedEmpty';
import { AgentThinking } from '@/components/agent/AgentThinking';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import { AIInputBar } from '@/components/ai/AIInputBar';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { RefreshCw, Keyboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipe } from '@/hooks/use-swipe';
import { useRealtime } from '@/hooks/use-realtime';

interface LayoutContext {
  user: { id: string; email: string };
  organizationId: string;
}

type FeedItem = 
  | { type: 'task'; data: TaskCardData }
  | { type: 'suggestion'; data: SuggestionData }
  | { type: 'insight'; data: InsightData };

export default function Feed() {
  const { user, organizationId } = useOutletContext<LayoutContext>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [undoItem, setUndoItem] = useState<{ item: FeedItem; action: string; index: number } | null>(null);

  const currentItem = feedItems[currentIndex];
  const hasItems = feedItems.length > currentIndex;

  // Fetch feed items (tasks + proactive messages)
  const fetchFeed = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch tasks assigned to user
      const { data: tasks, error: tasksError } = await supabase
        .from('sop_run_steps')
        .select(`
          id, title_snapshot, instruction_snapshot, status, due_at, 
          is_critical_snapshot, required_proof_type_snapshot,
          sop_runs!inner(name, organization_id, sop_templates(title))
        `)
        .eq('sop_runs.organization_id', organizationId)
        .eq('assigned_to_user_id', user.id)
        .in('status', ['NOT_STARTED', 'IN_PROGRESS'])
        .order('due_at', { ascending: true, nullsFirst: false })
        .limit(20);

      if (tasksError) throw tasksError;

      // Fetch proactive messages
      const { data: messages, error: messagesError } = await supabase
        .from('proactive_messages')
        .select('*')
        .eq('user_id', user.id)
        .eq('organization_id', organizationId)
        .eq('status', 'pending')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(10);

      if (messagesError) throw messagesError;

      // Transform to feed items
      const taskItems: FeedItem[] = (tasks || []).map(t => ({
        type: 'task' as const,
        data: {
          id: t.id,
          title: t.title_snapshot,
          instruction: t.instruction_snapshot,
          runName: t.sop_runs?.name,
          sopTitle: t.sop_runs?.sop_templates?.title,
          dueAt: t.due_at,
          isCritical: t.is_critical_snapshot,
          proofType: t.required_proof_type_snapshot,
          status: t.status,
        },
      }));

      const messageItems: FeedItem[] = (messages || []).map(m => {
        if (m.message_type === 'insight') {
          return {
            type: 'insight' as const,
            data: {
              id: m.id,
              title: m.title,
              value: (m.action_payload as any)?.value || '—',
              change: (m.action_payload as any)?.change,
              description: m.content,
              actionLabel: (m.action_payload as any)?.actionLabel,
              actionPayload: m.action_payload,
            } as InsightData,
          };
        }
        return {
          type: 'suggestion' as const,
          data: {
            id: m.id,
            type: m.message_type as SuggestionData['type'],
            title: m.title,
            content: m.content || '',
            priority: m.priority as SuggestionData['priority'],
            actionLabel: (m.action_payload as any)?.actionLabel,
            actionPayload: m.action_payload,
          } as SuggestionData,
        };
      });

      // Interleave: suggestions first (higher priority), then tasks
      const sortedFeed = [...messageItems, ...taskItems];
      setFeedItems(sortedFeed);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Error fetching feed:', error);
      toast({
        variant: 'destructive',
        title: 'Error loading feed',
        description: 'Could not load your tasks and suggestions.',
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId, user.id, toast]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  // Realtime subscription for proactive messages
  useRealtime<{ id: string; title: string; content: string; message_type: string; priority: string; action_payload: any }>({
    table: 'proactive_messages',
    event: 'INSERT',
    filter: `user_id=eq.${user.id}`,
    onInsert: (newMessage) => {
      // Add new message to front of feed
      const newItem: FeedItem = newMessage.message_type === 'insight'
        ? {
            type: 'insight' as const,
            data: {
              id: newMessage.id,
              title: newMessage.title,
              value: (newMessage.action_payload as any)?.value || '—',
              change: (newMessage.action_payload as any)?.change,
              description: newMessage.content,
              actionLabel: (newMessage.action_payload as any)?.actionLabel,
              actionPayload: newMessage.action_payload,
            } as InsightData,
          }
        : {
            type: 'suggestion' as const,
            data: {
              id: newMessage.id,
              type: newMessage.message_type as SuggestionData['type'],
              title: newMessage.title,
              content: newMessage.content || '',
              priority: newMessage.priority as SuggestionData['priority'],
              actionLabel: (newMessage.action_payload as any)?.actionLabel,
              actionPayload: newMessage.action_payload,
            } as SuggestionData,
          };
      
      setFeedItems(prev => [newItem, ...prev]);
      
      // Haptic feedback for new item
      if ('vibrate' in navigator) {
        navigator.vibrate([50, 30, 50]);
      }
      
      toast({
        title: newMessage.title,
        description: 'New suggestion from Ritual',
      });
    },
  });

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!hasItems || isProcessing) return;
      
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        if (currentItem?.type === 'task') {
          handleTaskComplete(currentItem.data.id);
        } else if (currentItem?.type === 'suggestion') {
          handleSuggestionAct(currentItem.data.id, currentItem.data.actionPayload);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'Escape') {
        e.preventDefault();
        if (currentItem?.type === 'task') {
          handleTaskSkip(currentItem.data.id);
        } else if (currentItem?.type === 'suggestion') {
          handleSuggestionDismiss(currentItem.data.id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasItems, isProcessing, currentItem, currentIndex]);

  // Handle task completion
  const handleTaskComplete = useCallback(async (taskId: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('sop_run_steps')
        .update({
          status: 'COMPLETED',
          completed_at: new Date().toISOString(),
        })
        .eq('id', taskId);

      if (error) throw error;

      // Store for undo
      const item = feedItems[currentIndex];
      setUndoItem({ item, action: 'completed', index: currentIndex });
      
      // Haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate(10);
      }
      
      // Move to next card
      setCurrentIndex(prev => prev + 1);
      
      toast({
        title: 'Task completed ✓',
        action: (
          <Button variant="ghost" size="sm" onClick={() => handleUndo(taskId)}>
            Undo
          </Button>
        ),
      });
    } catch (error) {
      console.error('Error completing task:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not complete task.',
      });
    } finally {
      setIsProcessing(false);
    }
  }, [feedItems, currentIndex, toast]);

  // Handle task skip
  const handleTaskSkip = useCallback(async (taskId: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('sop_run_steps')
        .update({
          status: 'SKIPPED',
          skipped_by_user_id: user.id,
        })
        .eq('id', taskId);

      if (error) throw error;

      const item = feedItems[currentIndex];
      setUndoItem({ item, action: 'skipped', index: currentIndex });
      
      // Haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate(5);
      }
      
      setCurrentIndex(prev => prev + 1);
      
      toast({
        title: 'Task skipped',
        action: (
          <Button variant="ghost" size="sm" onClick={() => handleUndo(taskId)}>
            Undo
          </Button>
        ),
      });
    } catch (error) {
      console.error('Error skipping task:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [user.id, feedItems, currentIndex, toast]);

  // Handle suggestion dismiss
  const handleSuggestionDismiss = useCallback(async (id: string) => {
    try {
      await supabase
        .from('proactive_messages')
        .update({ status: 'dismissed', dismissed_at: new Date().toISOString() })
        .eq('id', id);
      
      setCurrentIndex(prev => prev + 1);
    } catch (error) {
      console.error('Error dismissing suggestion:', error);
    }
  }, []);

  // Handle suggestion action
  const handleSuggestionAct = useCallback(async (id: string, payload?: any) => {
    try {
      await supabase
        .from('proactive_messages')
        .update({ status: 'acted', acted_at: new Date().toISOString() })
        .eq('id', id);
      
      // Handle specific actions
      if (payload?.route) {
        navigate(payload.route);
      }
      
      setCurrentIndex(prev => prev + 1);
    } catch (error) {
      console.error('Error acting on suggestion:', error);
    }
  }, [navigate]);

  // Undo handler
  const handleUndo = useCallback(async (taskId: string) => {
    if (!undoItem) return;
    
    try {
      await supabase
        .from('sop_run_steps')
        .update({
          status: 'NOT_STARTED',
          completed_at: null,
          skipped_by_user_id: null,
          skipped_reason: null,
        })
        .eq('id', taskId);
      
      setCurrentIndex(undoItem.index);
      setUndoItem(null);
      
      toast({ title: 'Action undone' });
    } catch (error) {
      console.error('Error undoing:', error);
    }
  }, [undoItem, toast]);

  // Swipe gesture for current card
  const [swipeState, swipeHandlers] = useSwipe({
    onSwipeLeft: () => {
      if (currentItem?.type === 'task') {
        handleTaskSkip(currentItem.data.id);
      } else if (currentItem?.type === 'suggestion') {
        handleSuggestionDismiss(currentItem.data.id);
      } else if (currentItem?.type === 'insight') {
        handleSuggestionDismiss(currentItem.data.id);
      }
    },
    onSwipeRight: () => {
      // Don't trigger here for tasks with proof - let TaskSwipeCard handle it
      if (currentItem?.type === 'suggestion') {
        handleSuggestionAct(currentItem.data.id, currentItem.data.actionPayload);
      } else if (currentItem?.type === 'insight') {
        handleSuggestionAct(currentItem.data.id, currentItem.data.actionPayload);
      }
    },
    disabled: isProcessing,
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <AgentThinking message="Loading your feed..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* AI Input Bar - mobile optimized */}
      <div className="px-4 pt-4 pb-2">
        <AIInputBar organizationId={organizationId} placeholder="Ask Ritual anything..." />
      </div>

      {/* Feed count */}
      {hasItems && (
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-2">
            <AgentAvatar size="sm" />
            <span className="text-sm text-muted-foreground">
              {feedItems.length - currentIndex} {feedItems.length - currentIndex === 1 ? 'item' : 'items'} to review
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchFeed}
            disabled={loading}
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </Button>
        </div>
      )}

      {/* Swipe deck */}
      <div className="flex-1 px-4 py-2 overflow-hidden">
        {!hasItems ? (
          <FeedEmpty
            onStartSOP={() => navigate('/app/library')}
            onAskAgent={() => {
              const input = document.querySelector('input[placeholder*="Ask Ritual"]') as HTMLInputElement;
              input?.focus();
            }}
          />
        ) : (
          <div className="relative h-[400px] sm:h-[420px]">
            <AnimatePresence mode="popLayout">
              {feedItems.slice(currentIndex, currentIndex + 3).map((item, idx) => {
                const isTop = idx === 0;
                const scale = 1 - idx * 0.03;
                const y = idx * 8;

                return (
                  <motion.div
                    key={item.data.id}
                    className="absolute inset-0"
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{
                      scale,
                      y,
                      opacity: 1 - idx * 0.15,
                      zIndex: 3 - idx,
                    }}
                    exit={{
                      x: swipeState.x > 0 ? 300 : -300,
                      opacity: 0,
                      rotate: swipeState.x > 0 ? 15 : -15,
                    }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    style={{
                      pointerEvents: isTop ? 'auto' : 'none',
                    }}
                  >
                    {item.type === 'task' && (
                      <TaskSwipeCard
                        task={item.data}
                        onComplete={handleTaskComplete}
                        onSkip={handleTaskSkip}
                        isTopCard={isTop}
                      />
                    )}
                    {item.type === 'suggestion' && (
                      <div
                        style={{
                          transform: isTop
                            ? `translate(${swipeState.x}px, ${swipeState.y}px) rotate(${swipeState.rotation}deg)`
                            : undefined,
                          transition: swipeState.isAnimating ? 'all 0.3s' : 'none',
                        }}
                        {...(isTop ? swipeHandlers : {})}
                      >
                        <SuggestionCard
                          suggestion={item.data}
                          onDismiss={handleSuggestionDismiss}
                          onAct={handleSuggestionAct}
                          isTopCard={isTop}
                        />
                      </div>
                    )}
                    {item.type === 'insight' && (
                      <div
                        style={{
                          transform: isTop
                            ? `translate(${swipeState.x}px, ${swipeState.y}px) rotate(${swipeState.rotation}deg)`
                            : undefined,
                          transition: swipeState.isAnimating ? 'all 0.3s' : 'none',
                        }}
                        {...(isTop ? swipeHandlers : {})}
                      >
                        <InsightCard
                          insight={item.data}
                          onDismiss={handleSuggestionDismiss}
                          onAct={handleSuggestionAct}
                          isTopCard={isTop}
                        />
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {/* Progress dots */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1.5 py-2">
              {feedItems.slice(0, Math.min(10, feedItems.length)).map((_, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'h-1.5 rounded-full transition-all',
                    idx === currentIndex
                      ? 'w-6 bg-primary'
                      : idx < currentIndex
                        ? 'w-1.5 bg-primary/50'
                        : 'w-1.5 bg-muted-foreground/30'
                  )}
                />
              ))}
              {feedItems.length > 10 && (
                <span className="text-xs text-muted-foreground ml-1">
                  +{feedItems.length - 10}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Swipe hints (mobile) */}
      {hasItems && (
        <div className="flex justify-between px-8 py-4 text-sm text-muted-foreground md:hidden">
          <span className="flex items-center gap-1">
            <span className="text-lg">←</span> Skip
          </span>
          <span className="flex items-center gap-1">
            Complete <span className="text-lg">→</span>
          </span>
        </div>
      )}

      {/* Keyboard hints (desktop) */}
      {hasItems && (
        <div className="hidden md:flex justify-center items-center gap-6 py-4 text-sm text-muted-foreground border-t border-border/50">
          <div className="flex items-center gap-2">
            <Keyboard className="h-4 w-4" />
            <span>Keyboard:</span>
          </div>
          <span>
            <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">←</kbd> Skip
          </span>
          <span>
            <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">→</kbd> Complete
          </span>
        </div>
      )}
    </div>
  );
}
